import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective, UntypedFormGroup } from '@angular/forms';
import { CodeConversionMstDto, SuppntInfo } from 'sg-api';

/**
 * 描画状態emum
 *
 * @enum {number}
 */
enum DrawState {
  /** 初期化 */
  init,
  /** 押し下げ */
  drag,
  /** 解放 */
  release,
}

@Component({
  selector: 'app-sgke360d-last-confirmation',
  templateUrl: './sgke360d-last-confirmation.component.html',
  styleUrl: './sgke360d-last-confirmation.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke360dLastConfirmationComponent implements OnInit, OnDestroy, AfterViewInit {
  /** 基本情報のレスポンス */
  @Input() suppntInfo: SuppntInfo = null;
  /** 最終確認タブ情報 */
  @Input() finalConfirmTab;
  /** コード変換マスタマップ */
  @Input() codeMap: Map<string, CodeConversionMstDto[]> = null;
  /** 作業種別 */
  public sagyoshubetsuCD: string = '2';
  /** 署名欄サイズ */
  public canvasSize = { w: 870, h: 300 };
  /** 現描画座標 */
  private lastPos = { x: null, y: null };
  /** 描画状態 */
  private drawState: DrawState = DrawState.init;

  /** 署名欄キャンバス */
  @ViewChild("signCanvas", { static: false }) signCanvas: ElementRef;
  canvas: HTMLCanvasElement;
  /** キャンバス描画コンテキスト*/
  cx: CanvasRenderingContext2D;

  /** 親フォームグループ */
  public parentForm: UntypedFormGroup;

  /** チラシ投函チェックボックス */
  public get flyerPostingFlg(): boolean {
    // マイコンメータ無
    let enabled = this.getControl('microcontrollermeterUmuFlg').value == '0';
    // または業務引継ぎが有効
    enabled ||= this.getControl('inspectiontaskTakeovertoCode').enabled;
    // または確認結果が合格
    enabled ||= this.getControl('leaktestConfirmationresultFlg').value == '1';
    const ctl = this.getControl('flyerPostingFlg');
    if (enabled) {
      ctl.enable();
    } else {
      ctl.disable();
      ctl.setValue('0');
    }
    return ctl?.value == '1';
  }
  public set flyerPostingFlg(v: boolean) {
    this.getControl('flyerPostingFlg')?.setValue(v ? '1' : '0');
  }

  /** */
  public get signatureDivValue() {
    const disabled = !this.getControl('lightingExaminedFlg').value;
    const ctl = this.getControl('signatureDiv');
    if (disabled) {
      ctl.disable();
      ctl.setValue(null);
    } else {
      ctl.enable();
    }
    return ctl.value;
  }
  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {CodeConversionMstService} codeConvert
   * @memberof Sgke360dLastConfirmationComponent
   */
  constructor(
    private parent: FormGroupDirective,
  ) { }

  /**
   * 初期化
   *
   * @memberof Sgke360dLastConfirmationComponent
   */
  ngOnInit(): void {
    // 作業種別コード
    this.sagyoshubetsuCD = this.suppntInfo.workClscode;

    // 親フォームを参照
    this.parentForm = this.parent.form.get('supplyresumptionResultInfo') as UntypedFormGroup;

  }

  /**
   * 終了処理
   *
   * @memberof Sgke360dLastConfirmationComponent
   */
  ngOnDestroy(): void {
  }

  /**
   * 初期化後処理
   *
   * @memberof Sgke360dLastConfirmationComponent
   */
  ngAfterViewInit(): void {
    if (this.sagyoshubetsuCD == '2') {
      // キャンバス取得
      const canvasEl: HTMLCanvasElement = this.signCanvas.nativeElement;
      this.canvas = canvasEl;
      this.cx = canvasEl.getContext("2d");
      // プレースホルダー描画
      this.drawPlaceholder();

      // マウスイベント用
      canvasEl.addEventListener("mousedown", (e) => this.dragStart(e.offsetX, e.offsetY));
      canvasEl.addEventListener("mouseup", (e) => this.dragEnd());
      canvasEl.addEventListener("mouseout", (e) => this.dragEnd());
      canvasEl.addEventListener("mousemove", (e) => this.draw(e.offsetX, e.offsetY));

      // タッチイベント用
      canvasEl.addEventListener("touchstart", (e) => this.dragStart());
      canvasEl.addEventListener("touchcancel", (e) => this.dragEnd());
      canvasEl.addEventListener("touchend", (e) => this.dragEnd());
      canvasEl.addEventListener("touchmove", (e) => {
        // スワイプ無効
        e.preventDefault();
        // 座標計算
        let x = e.touches[0].clientX - canvasEl.getBoundingClientRect().left;
        let y = e.touches[0].clientY - canvasEl.getBoundingClientRect().top;
        this.draw(x, y);
      });

      // 線端の形状, 太さ, 色
      this.cx.lineCap = "round";
      this.cx.lineWidth = 5;
      this.cx.strokeStyle = "black";
    }
  }

  /**
   * 線描画開始
   *
   * @param {number} [x] X座標
   * @param {number} [y] Y座標
   * @memberof Sgke360dLastConfirmationComponent
   */
  private dragStart(x?: number, y?: number) {
    this.cx.beginPath();
    this.cx.moveTo(x, y);
    this.setDrawState(DrawState.drag);
  };

  /**
   * 線描画終了
   *
   * @memberof Sgke360dLastConfirmationComponent
   */
  private dragEnd() {
    this.cx.closePath();
    // 初期化状態での解放状態（描画範囲外移動）遷移は初期化のまま。
    const nextState = this.drawState == DrawState.init ? DrawState.init : DrawState.release;
    this.setDrawState(nextState);
    // 座標リセット
    this.lastPos = { x: null, y: null };
  };

  /**
   * 描画処理
   *
   * @param {number} x X座標
   * @param {number} y Y座標
   * @memberof Sgke360dLastConfirmationComponent
   */
  private draw(x: number, y: number) {
    // マウス/タッチ押し下げ以外は描画しない
    if (this.drawState == DrawState.drag) {
      if (this.lastPos?.x === null || this.lastPos?.y === null) {
        this.cx.moveTo(x, y);
      } else {
        this.cx.moveTo(this.lastPos.x, this.lastPos.y);
      }
      this.cx.lineTo(x, y);
      this.cx.stroke();

      // 座標保持
      this.lastPos = { x: x, y: y };
    }
  }

  /**
   * 描画状態設定
   *
   * @param {DrawState} [drawState] 描画状態
   * @memberof Sgke360dLastConfirmationComponent
   */
  public setDrawState(drawState?: DrawState) {
    if (drawState == null || drawState == DrawState.init) {
      // 初期化時は領域クリアと、プレースホルダ描画
      this.cx.clearRect(0, 0, this.canvasSize.w, this.canvasSize.h);
      this.drawPlaceholder();
    } else if (drawState == DrawState.drag && this.drawState == DrawState.init) {
      // 初期化→押し下げ状態ではプレースホルダ削除するため、領域をクリア
      this.cx.clearRect(0, 0, this.canvasSize.w, this.canvasSize.h);
    }
    // 描画状態の更新
    this.drawState = drawState ?? DrawState.init;
  }

  /**
   * プレースホルダー描画処理
   *
   * @private
   * @memberof Sgke360dLastConfirmationComponent
   */
  private drawPlaceholder() {
    const text = '署名入力エリア';
    this.cx.font = '20px Arial';
    this.cx.fillStyle = 'gray';
    const textWidth = this.cx.measureText(text).width;
    // フォントサイズに合わせて調整要
    const textHeight = 20;
    const x = (this.signCanvas.nativeElement.width - textWidth) / 2;
    const y = (this.signCanvas.nativeElement.height + textHeight) / 2;
    this.cx.fillText(text, x, y);
  }

  /**
   * 署名画像ファイル取得
   *
   * @param {string} fileName ファイル名
   * @param {string} [fileType] ファイル種別
   * @return {*}  {Promise<File>} 署名画像取得のPromise
   * @memberof Sgke360dLastConfirmationComponent
   */
  public getImageFile(fileName: string, fileType?: string): Promise<File> {
    return new Promise(resolve => {
      this.canvas?.toBlob(blob => {
        resolve(new File([blob], fileName));
      }, fileType);
    });
  }


  /**
   * フォームコントロール取得
   * @param ctlname コントロール名
   * @returns フォームコントロール
   */
  getControl(ctlname: string): FormControl {
    return this.parentForm?.get(ctlname) as FormControl;
  }

}
